import React from 'react';
import Icon from '../../../components/AppIcon';

const InvoiceTable = ({ transactions, loading, onViewDetails }) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      succeeded: { color: 'bg-green-100 text-green-700', label: 'Paid', icon: 'CheckCircle' },
      pending: { color: 'bg-yellow-100 text-yellow-700', label: 'Pending', icon: 'Clock' },
      failed: { color: 'bg-red-100 text-red-700', label: 'Failed', icon: 'XCircle' },
      processing: { color: 'bg-blue-100 text-blue-700', label: 'Processing', icon: 'Loader' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon name={config?.icon} className="w-3.5 h-3.5" />
        {config?.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatAmount = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency?.toUpperCase()
    })?.format(amount / 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (transactions?.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
          <Icon name="FileText" className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No invoices yet</h3>
        <p className="text-gray-600">Your payment history will appear here</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Invoice ID
            </th>
            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Date
            </th>
            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Description
            </th>
            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Amount
            </th>
            <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {transactions?.map((transaction) => (
            <tr key={transaction?.id} className="hover:bg-gray-50 transition-colors">
              <td className="py-4 px-4">
                <span className="text-sm font-medium text-gray-900">
                  {transaction?.stripeInvoiceId?.substring(0, 12) || 'N/A'}
                </span>
              </td>
              <td className="py-4 px-4">
                <span className="text-sm text-gray-600">
                  {formatDate(transaction?.createdAt)}
                </span>
              </td>
              <td className="py-4 px-4">
                <span className="text-sm text-gray-900">
                  {transaction?.description || 'Subscription Payment'}
                </span>
              </td>
              <td className="py-4 px-4">
                <span className="text-sm font-medium text-gray-900">
                  {formatAmount(transaction?.amount, transaction?.currency)}
                </span>
              </td>
              <td className="py-4 px-4">
                {getStatusBadge(transaction?.status)}
              </td>
              <td className="py-4 px-4 text-right">
                <button
                  onClick={() => onViewDetails(transaction)}
                  className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  <Icon name="Eye" className="w-4 h-4" />
                  View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default InvoiceTable;